<template>
  <FormControl :title="title" :sub-title="subTitle">
    <div v-if="!showResults">
      <RangeChoiceForBlock
        v-if="type === 'RANGE'"
        :id="id"
        :options="options"
        :prefix="prefix"
        :suffix="suffix"
        :is-selected="isSelected"
        @handle-selection="handleSelection"/>
      <div
        class="flex flex-col gap-4"
        :class="{ '!flex-row': type === 'CARD_CHOICE' }">
        <template v-for="(item, index) in options" :key="item.id">
          <ChoiceOption
            v-if="type === 'CHOICE'"
            :id="item.id"
            :key="`choice-${item.id}`"
            :item="item"
            :selected="isSelected(item.id)"
            :index="index"
            readonly
            :disabled="review"
            @select-item="handleSelection(item.id)"/>
          <CardChoice
            v-if="type === 'CARD_CHOICE'"
            :id="item.id"
            :key="`card-${item.id}`"
            :item="item"
            :format="'image-text'"
            :selected="isSelected(item.id)"
            readonly
            :disabled="review"
            @select-item="handleSelection(item.id)"/>
        </template>
      </div>
      <PollSupportCard
        v-if="type === 'SUPPORT_CHOICE'"
        :id="id"
        v-model="model"
        :options="options"/>
    </div>

    <div v-else class="poll-result-wrapper">
      <BarsChart
        :bars-data="votesData"
        :horizontal="type === 'CHOICE'"
        :percentage-results="percentageResults"
        :amount-results="amountResults"/>
    </div>
  </FormControl>
</template>

<script setup lang="ts">
import { computed, onMounted, PropType, ref, watch } from 'vue';
import FormControl from '@/components/utils/FormControl/FormControl.vue';
import { useVModel } from '@vueuse/core';
import PollSupportCard from './PollSupportCard.vue';
import { ChoiceOption, CardChoice } from '@amit/components';
import type { PollOption, PollSummary } from './poll';
import { BarsChart } from '@amit/components';
import RangeChoiceForBlock from '../choice/RangeChoiceForBlock.vue';

const props = defineProps({
  modelValue: {
    type: Array<string>,
    default: () => [],
  },
  id: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    default: '',
  },
  subTitle: {
    type: String,
    default: '',
  },
  resultType: {
    type: String,
    default: '',
  },
  options: {
    type: Array as PropType<PollOption[]>,
    default: () => [],
  },
  prefix: {
    type: String,
    default: '',
  },
  suffix: {
    type: String,
    default: '',
  },
  percentageResults: {
    type: Boolean,
    default: false,
  },
  amountResults: {
    type: Boolean,
    default: true,
  },
  answers: {
    type: Array as PropType<PollSummary[]>,
    default: () => [],
  },
  review: {
    type: Boolean,
    default: false,
  },
});

const model = useVModel(props, 'modelValue');
const votesData = ref<PollSummary[]>([]);
const showResults = computed<boolean>(() => {
  return props.answers.some(answer => answer.currentUserVote);
});

const isSelected = (id: string) => model.value.includes(id);

onMounted(() => {
  props.options.forEach((option: { id: string; title: string }) => {
    votesData.value.push({
      id: option.id,
      title: option.title,
      count: 0,
      currentUserVote: false,
    });
  });
});

watch(
  () => props.answers,
  newAnswers => {
    newAnswers.forEach(answer => {
      const vote = votesData.value.find(item => item.id === answer.id);
      if (vote) {
        vote.count = answer.count;
        vote.currentUserVote = answer.currentUserVote;
      }
    });
  },
);

const handleSelection = (id: string) => {
  model.value = [id];
};
</script>
